import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCircle, WarningCircle, X, ClipboardText } from "@phosphor-icons/react";
import { api } from "@/lib/api";

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/notifications");
      setItems(res.data);
    } catch {
      // silent — bell shows last good list
    } finally {
      setLoading(false);
    }
  };

  // Poll every 30s while signed in.
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  // Click outside closes the dropdown
  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const unread = items.filter((n) => !n.read).length;

  const markRead = async (id) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    try {
      await api.post(`/notifications/${id}/read`);
    } catch {
      // best effort
    }
  };

  const markAllRead = async () => {
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    try {
      await api.post("/notifications/read-all");
    } catch {
      // best effort
    }
  };

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="notification-bell"
        onClick={() => setOpen((v) => !v)}
        className="relative inline-flex h-10 w-10 items-center justify-center rounded-md text-gray-700 hover:bg-gray-100"
        aria-label={unread ? `${unread} unread notifications` : "Notifications"}
      >
        <Bell size={20} weight={unread ? "fill" : "regular"} />
        {unread > 0 && (
          <span
            data-testid="notification-unread-count"
            className="absolute right-1 top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[#EB1D25] px-1 text-[10px] font-bold leading-none text-white"
          >
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notification-dropdown"
          className="absolute right-0 top-12 z-40 w-96 max-w-[calc(100vw-2rem)] overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg"
        >
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-2.5">
            <div className="text-sm font-semibold text-gray-900">Notifications</div>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button
                  data-testid="notification-mark-all-read"
                  onClick={markAllRead}
                  className="rounded-md px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100"
                >
                  Mark all read
                </button>
              )}
              <button
                onClick={() => setOpen(false)}
                className="rounded-md p-1 text-gray-500 hover:bg-gray-100"
                aria-label="Close"
              >
                <X size={14} weight="bold" />
              </button>
            </div>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading && items.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">Loading…</div>
            ) : items.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-gray-500">
                No notifications yet.
              </div>
            ) : (
              items.map((n) => (
                <button
                  key={n.id}
                  data-testid={`notification-item-${n.id}`}
                  onClick={() => {
                    markRead(n.id);
                    if (n.ref_type === "task" && n.ref_id) {
                      setOpen(false);
                      navigate(`/tasks?task=${encodeURIComponent(n.ref_id)}`);
                    } else if (n.order_id) {
                      setOpen(false);
                      navigate(`/todays-orders?order=${encodeURIComponent(n.order_id)}`);
                    }
                  }}
                  className={`flex w-full items-start gap-3 border-b border-gray-100 px-4 py-3 text-left last:border-0 hover:bg-gray-50 ${
                    n.read ? "" : "bg-blue-50/30"
                  }`}
                >
                  <div className="mt-0.5 shrink-0">
                    {n.kind === "order_failed" ? (
                      <WarningCircle size={18} weight="fill" className="text-[#EB1D25]" />
                    ) : n.kind === "task" ? (
                      <ClipboardText size={18} weight="fill" className="text-blue-600" />
                    ) : (
                      <CheckCircle size={18} weight="fill" className="text-green-600" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <div className="truncate text-sm font-semibold text-gray-900">
                        {n.title}
                      </div>
                      {!n.read && (
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#EB1D25]" />
                      )}
                    </div>
                    <div className="mt-0.5 truncate text-xs text-gray-600">{n.message}</div>
                    <div className="mt-1 text-[11px] text-gray-400">
                      {formatRelative(n.created_at)}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function formatRelative(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diff = Math.round((Date.now() - then) / 1000);
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)} h ago`;
  return new Date(iso).toLocaleDateString("en-CA", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
